import React, { useEffect, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { LayoutDashboard, ReceiptText } from 'lucide-react';
import { locataireApi, downloadPdfWithAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { Download, Home } from 'lucide-react';
import { Loader } from '@/components/Loader';

const links = [
  { to: '/locataire', end: true, label: 'Mon espace', icon: LayoutDashboard },
];

const STATUS_META = {
  paid: { label: 'Payé', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial: { label: 'Partiel', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  late: { label: 'En retard', cls: 'bg-red-50 text-red-700 border-red-200' },
  pending: { label: 'À venir', cls: 'bg-slate-50 text-slate-700 border-slate-200' },
};

export default function LocataireDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await locataireApi.dashboard(); setData(data); }
      finally { setLoading(false); }
    })();
  }, []);

  const dl = async (p) => {
    try { await downloadPdfWithAuth(locataireApi.recuUrl(p.id), `${p.recu_numero}.pdf`); }
    catch { toast.error('Téléchargement impossible'); }
  };

  return (
    <AppLayout links={links} testid="locataire-sidebar" title={loading ? '' : `Bonjour ${data.locataire.prenom}`}>
      {loading ? (
        <Loader variant="full" />
      ) : data ? (
        <>
          <p className="text-slate-500 -mt-4 mb-6 text-sm">Voici vos paiements et l'état de vos échéances.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Loyer mensuel</div>
              <div className="mt-2 text-3xl font-bold text-[#0D1B2A]">
                {data.locataire.montant_loyer} <span className="text-sm">{data.locataire.devise}</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Le {data.locataire.jour_echeance} de chaque mois</div>
            </div>
            <div className="rounded-xl border p-5 bg-[#0D1B2A] text-white border-[#0D1B2A]">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-white/70">Total payé</div>
              <div className="mt-2 text-3xl font-bold">
                {data.kpis.total_paye} <span className="text-sm">{data.kpis.devise}</span>
              </div>
              <div className="text-xs text-white/60 mt-1">Depuis le début du bail</div>
            </div>
            <div className="rounded-xl border p-5 bg-white border-slate-200">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Bailleur</div>
              {data.bailleur ? (
                <>
                  <div className="mt-2 text-lg font-bold text-[#0D1B2A]">{data.bailleur.prenom} {data.bailleur.nom}</div>
                  <div className="text-xs text-slate-500">{data.bailleur.telephone}</div>
                </>
              ) : <div className="mt-2 text-sm text-slate-500">—</div>}
            </div>
          </div>

          <div className="mt-8 lb-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <Home className="w-4 h-4 text-[#1677FF]" />
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Mon logement</div>
            </div>
            <div className="text-sm text-slate-700">{data.locataire.adresse_logement}</div>
          </div>

          <div className="mt-6 lb-card overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Historique des paiements</div>
            </div>
            <div className="overflow-x-auto" data-testid={TID.locHistory}>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    {['N° Reçu', 'Période', 'Date', 'Montant', 'Mode', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.paiements.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Aucun paiement enregistré pour le moment.</td></tr>
                  )}
                  {data.paiements.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-mono text-xs">{p.recu_numero}</td>
                      <td className="px-4 py-3">{p.recu_periode}</td>
                      <td className="px-4 py-3">{new Date(p.date_paiement).toLocaleDateString('fr-FR')}</td>
                      <td className="px-4 py-3 font-bold text-[#0D1B2A]">{p.montant} {p.devise}</td>
                      <td className="px-4 py-3 text-xs uppercase text-slate-600">{p.mode}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" variant="outline" data-testid={TID.locDownload(p.id)} onClick={() => dl(p)}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Reçu PDF
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 lb-card p-6">
            <div className="flex items-center gap-2 mb-3">
              <ReceiptText className="w-4 h-4 text-[#1677FF]" />
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Échéances</div>
            </div>
            <div className="divide-y divide-slate-100">
              {data.echeances.slice(0, 12).map((e) => {
                const meta = STATUS_META[e.statut] || STATUS_META.pending;
                return (
                  <div key={e.id} className="py-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-[#0D1B2A]">{e.periode_label}</div>
                      <div className="text-xs text-slate-500">Échéance : {new Date(e.date_echeance).toLocaleDateString('fr-FR')}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className="font-bold text-[#0D1B2A]">{e.montant_du} {e.devise}</div>
                        <div className="text-xs text-slate-500">Payé : {e.montant_paye || 0}</div>
                      </div>
                      <Badge className={`${meta.cls} border`}>{meta.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      ) : null}
    </AppLayout>
  );
}
